// ui/RealGame.jsx: first wire-up of the real engine into a playable
// canvas view (M2.c-10c). The demos in App.jsx still exist; this
// component is mounted via a toggle so playtesting can hop between
// scripted demo states and a live engine-driven game.
//
// What 10c covers: bot scheduler drives any non-human turn through
// useGameLoop. Human seat 1 still has BiddingControls / hand clicks
// from M2.c-2/3.
//
// What 10d will add: lifecycle (deal -> bid -> trick -> score ->
// next_hand) + dealer rotation + deterministic seed stream.
//
// What 10e will add: game-over overlay + Play Again button.
//
// What 11 will add: dealer-discard UI for human dealer.

import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'preact/hooks';
import { computeTableLayout } from '../canvas/layout.js';
import { readDevicePixelRatio, watchDevicePixelRatio, prepareHiDpiCanvas, backingSize, keepStageAligned } from '../canvas/hidpi.js';
import { renderTable, describeAriaLabel } from '../canvas/table.js';
import { renderScoreboard } from '../canvas/scoreboard.js';
import { renderAllHands } from '../canvas/hand.js';
import { renderAnnotations } from '../canvas/annotations.js';
import { renderUpCard, computeUpCardRect } from '../canvas/upcard.js';
import { renderTrickArea } from '../canvas/trick-area.js';
import { _internals as cardInternals } from '../canvas/cards.js';
import { BiddingControls } from './BiddingControls.jsx';
import { DiscardPrompt } from './DiscardPrompt.jsx';
import { EndOfHandModal } from './EndOfHandModal.jsx';
import { handSummaryFromSnapshot } from './hand-summary.js';
import { GameOverOverlay } from './GameOverOverlay.jsx';
import { useGameLoop } from './useGameLoop.js';
import { useBotScheduler } from './useBotScheduler.js';
import { useBotAnnotations } from './useBotAnnotations.js';
import { useEuchreEngine } from './useEuchreEngine.js';
import { useHandLifecycle, makeSeedStream } from './useHandLifecycle.js';
import { usePrefersReducedMotion } from './useTrickAnimation.js';
import { createCardSnapDetector } from './card-snap.js';
import * as audio from '../audio/events.js';

const { SUIT_NAME } = cardInternals;

const HUMAN_SEAT = 1;

/**
 * @param {{
 *   settings: {
 *     stick_the_dealer: boolean,
 *     heuristic_disclosure: boolean,
 *     reduced_motion_override?: boolean,
 *   },
 *   firstSeed?: number,
 *   forceDealerSeat?: 1|2|3|4,
 * }} props
 */
export function RealGame({ settings, firstSeed, forceDealerSeat }) {
  /** @type {{ current: HTMLCanvasElement | null }} */
  const canvasRef = useRef(null);
  /** @type {{ current: HTMLDivElement | null }} */
  const stageRef = useRef(null);
  const [dpr, setDpr] = useState(() => readDevicePixelRatio());
  useEffect(() => watchDevicePixelRatio(setDpr), []);
  const [layout, setLayout] = useState(() =>
    computeTableLayout(window.innerWidth, window.innerHeight));
  const [hoveredCardId, setHoveredCardId] = useState(/** @type {string|null} */ (null));
  const [focusedCardId, setFocusedCardId] = useState(/** @type {string|null} */ (null));
  /** @type {[any[], (h:any)=>void]} */
  const [playerHits, setPlayerHits] = useState(/** @type {any[]} */ ([]));
  const [selectedDiscard, setSelectedDiscard] = useState(/** @type {string|null} */ (null));
  // M5 #4, card-snap detector, persists across renders so per-card
  // "first seen" is tracked correctly.
  const cardSnapRef = useRef(/** @type {ReturnType<typeof createCardSnapDetector>|null} */ (null));
  if (cardSnapRef.current === null) cardSnapRef.current = createCardSnapDetector();

  const osReducedMotion = usePrefersReducedMotion();
  const reducedMotion = osReducedMotion || !!settings.reduced_motion_override;
  const { annotations, pushAnnotation } = useBotAnnotations(reducedMotion);

  const { game, status, error } = useEuchreEngine({
    stick_the_dealer: settings.stick_the_dealer,
    game_target: 10,
    heuristic_disclosure: settings.heuristic_disclosure,
  });

  const [state, dispatch, actionCreators] = useGameLoop(game);
  const snapshot = state.snapshot;
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    return keepStageAligned(stage, dpr);
  }, [dpr, !!snapshot]);

  // M2.c-10d deterministic seed stream. One seed feeds the whole game
  // so a bug report can replay every hand from the first-game seed.
  /** @type {{ current: ReturnType<typeof makeSeedStream>|null }} */
  const seedStreamRef = useRef(null);
  const dealtRef = useRef(false);
  useEffect(() => {
    if (!game) {
      dealtRef.current = false;
      seedStreamRef.current = null;
      return;
    }
    if (dealtRef.current) return;
    dealtRef.current = true;
    const initialSeed = firstSeed ?? (Date.now() % 0x7fffffff);
    seedStreamRef.current = makeSeedStream(initialSeed);
    if (forceDealerSeat) {
      // Test hook, pin the first-hand dealer. Calling setDealer
      // BEFORE deal because deal() reads dealer_seat to derive the
      // bidding lead seat.
      try { game.setDealer(forceDealerSeat); } catch { /* ignore */ }
    }
    dispatch(actionCreators.deal(seedStreamRef.current.next()));
  }, [game, dispatch, actionCreators, firstSeed]);

  // M2.c-10d hand lifecycle, auto-SCORE when the engine hits 'score'
  // state, then advance to next hand on Continue.
  const { continueHand } = useHandLifecycle({
    snapshot, dispatch, actionCreators, luaGameState: game,
    seedStream: seedStreamRef,
  });

  // M2.c-10c bot scheduler, drives any non-human turn.
  useBotScheduler({
    snapshot, dispatch, actionCreators, luaGameState: game,
    humanSeat: HUMAN_SEAT,
    reducedMotion,
    onBotAnnotation: (seat, text) => {
      if (settings.heuristic_disclosure) pushAnnotation(seat, text);
    },
  });

  useEffect(() => {
    let timer;
    function onResize() {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setLayout(computeTableLayout(window.innerWidth, window.innerHeight));
      }, 150);
    }
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Canvas render, every snapshot change re-renders.
  useEffect(() => {
    if (!canvasRef.current || !snapshot) return;
    const ctx = prepareHiDpiCanvas(canvasRef.current, layout.canvasW, layout.canvasH, dpr);
    if (!ctx) return;
    renderTable(ctx, snapshot, layout);
    renderScoreboard(ctx, snapshot, layout);
    if (snapshot.current_state === 'bid_r1'
        || snapshot.current_state === 'bid_r2'
        || snapshot.current_state === 'dealer_discard') {
      renderUpCard(ctx, snapshot, layout);
    }
    const hits = renderAllHands(ctx, layout, snapshot, { hoveredCardId, focusedCardId });
    setPlayerHits(hits);
    let trickCards = [];
    if (snapshot.trick_plays !== undefined) {
      trickCards = renderTrickArea(ctx, snapshot, layout, { trick_resolution: null });
      // M5 #4, fire card_snap audio per newly-landed card. The
      // detector emits audio.emit('card_snap') which the wired engine
      // picks up; if the sample isn't yet shipped (M5 #4 has the file
      // landing later) the engine no-ops. Visual scale-punch handled
      // in M5 #4 follow-up; the auditory half of the coupled event
      // lands here.
      cardSnapRef.current?.detect(snapshot);
    }
    renderAnnotations(ctx, layout, annotations);
    // Drawn-card records (CSS px) for browser tests: the human's hand hit
    // zones and the trick cards this frame.
    canvasRef.current.dataset.drawnCards = JSON.stringify({ hand: hits, trick: trickCards });
  }, [layout, snapshot, hoveredCardId, focusedCardId, annotations, dpr]);

  const handleBid = useCallback((bid) => {
    if (!snapshot) return;
    dispatch(actionCreators.submitBid(HUMAN_SEAT, bid));
  }, [snapshot, dispatch, actionCreators]);

  const handlePlayAgain = useCallback(() => {
    if (!game) return;
    // Reset the engine directly (synchronous), then dispatch a fresh
    // deal via the seed stream. We bypass RESET_GAME-as-queued-command
    // here because the queue would overwrite either reset or deal
    // if both were dispatched immediately. The reducer's snapshot is
    // momentarily stale until the deal's COMMAND_APPLIED lands.
    game.reset({
      stick_the_dealer: settings.stick_the_dealer,
      game_target: 10,
    });
    game.startBots({ heuristic_disclosure: settings.heuristic_disclosure });
    seedStreamRef.current = makeSeedStream(Date.now() % 0x7fffffff);
    dispatch(actionCreators.deal(seedStreamRef.current.next()));
  }, [game, settings.stick_the_dealer, settings.heuristic_disclosure, dispatch, actionCreators]);

  const handleCardClick = useCallback((cardId) => {
    if (!snapshot || !game) return;
    if (snapshot.current_state === 'trick_play'
        && snapshot.current_turn_seat === HUMAN_SEAT) {
      const legal = game.legalPlays(HUMAN_SEAT);
      if (!legal.includes(cardId)) {
        // M5 #6, illegal-click audio. The card stays put; the existing
        // illegal-flash CSS handles the visual cue (index.html). Audio
        // engine no-ops if the sample is missing.
        audio.emit('illegal_play', { card_id: cardId });
        return;
      }
      dispatch(actionCreators.playCard(HUMAN_SEAT, cardId));
      return;
    }
    if (snapshot.current_state === 'dealer_discard'
        && snapshot.dealer_seat === HUMAN_SEAT) {
      // Toggle selection: clicking the same card clears it.
      setSelectedDiscard(prev => prev === cardId ? null : cardId);
    }
  }, [snapshot, game, dispatch, actionCreators]);

  const handleDiscardConfirm = useCallback(() => {
    if (!selectedDiscard) return;
    dispatch(actionCreators.dealerDiscard(selectedDiscard));
    setSelectedDiscard(null);
  }, [selectedDiscard, dispatch, actionCreators]);

  // Clear any stale discard selection if we leave the discard state.
  useEffect(() => {
    if (!snapshot) return;
    if (snapshot.current_state !== 'dealer_discard') {
      if (selectedDiscard !== null) setSelectedDiscard(null);
    }
  }, [snapshot, selectedDiscard]);

  if (status === 'loading') {
    return <div data-testid="real-game-loading" style={{padding:'2em'}}>Loading engine…</div>;
  }
  if (status === 'error') {
    return <div data-testid="real-game-error" style={{padding:'2em',color:'#E03048'}}>
      Engine failed to start: {error}
    </div>;
  }
  if (!snapshot) {
    return <div data-testid="real-game-init" style={{padding:'2em'}}>Initializing…</div>;
  }

  const isBidding = snapshot.current_state === 'bid_r1' || snapshot.current_state === 'bid_r2';
  const isHumanBid = isBidding && snapshot.current_turn_seat === HUMAN_SEAT;
  const isHandOver = (snapshot.current_state === 'score'
                      || snapshot.current_state === 'end_of_hand')
    && !!snapshot.score_summary
    && !snapshot.game_over;
  const isGameOver = !!snapshot.game_over;
  const isHumanDiscard = snapshot.current_state === 'dealer_discard'
                         && snapshot.dealer_seat === HUMAN_SEAT;

  return (
    <div data-testid="real-game-root">
      <div
        ref={stageRef}
        style={{
          position: 'relative',
          width: layout.canvasW,
          height: layout.canvasH,
        }}
      >
        <canvas
          ref={canvasRef}
          id="game-canvas"
          width={backingSize(layout.canvasW, layout.canvasH, dpr).width}
          height={backingSize(layout.canvasW, layout.canvasH, dpr).height}
          role="img"
          aria-label={describeAriaLabel(snapshot)}
          style={{ display: 'block', width: `${layout.canvasW}px`, height: `${layout.canvasH}px` }}
        />
        {playerHits.map((hit) => {
          const suit = hit.card_id[1];
          const rank = hit.card_id[0] === 'T' ? 'Ten' : hit.card_id[0];
          const label = hit.bower
            ? `${rank} of ${SUIT_NAME[suit]} (${hit.bower} bower)`
            : `${rank} of ${SUIT_NAME[suit]}`;
          // In dealer-discard mode all 6 cards are clickable (the
          // dealer can discard ANY of them, including the just-picked-
          // up card). canvas/hand.js renders the up-card as the 6th
          // card in the dealer's hand, so the hit list already
          // includes it.
          const isSelectedDiscard = isHumanDiscard && selectedDiscard === hit.card_id;
          const clickable = isHumanDiscard ? true : hit.legal;
          return (
            <button
              key={hit.card_id}
              type="button"
              data-card-id={hit.card_id}
              data-discard-selected={isSelectedDiscard ? 'true' : undefined}
              aria-label={label}
              aria-pressed={isHumanDiscard ? isSelectedDiscard : undefined}
              aria-disabled={!clickable}
              onMouseEnter={() => setHoveredCardId(hit.card_id)}
              onMouseLeave={() => setHoveredCardId(null)}
              onFocus={() => setFocusedCardId(hit.card_id)}
              onBlur={() => setFocusedCardId(null)}
              onClick={() => handleCardClick(hit.card_id)}
              style={{
                position: 'absolute',
                left: hit.x, top: hit.y, width: hit.w, height: hit.h,
                background: 'transparent',
                border: isSelectedDiscard ? '3px solid #E03048' : 'none',
                borderRadius: isSelectedDiscard ? 6 : 0,
                padding: 0,
                cursor: clickable ? 'pointer' : 'default',
                outline: 'none',
              }}
            />
          );
        })}
        {isHumanDiscard && snapshot.up_card && (() => {
          const rect = computeUpCardRect(layout);
          const isSel = selectedDiscard === snapshot.up_card;
          const suit = snapshot.up_card[1];
          const rank = snapshot.up_card[0] === 'T' ? 'Ten' : snapshot.up_card[0];
          return (
            <button
              type="button"
              data-card-id={snapshot.up_card}
              data-up-card="true"
              data-discard-selected={isSel ? 'true' : undefined}
              aria-label={`Up-card: ${rank} of ${SUIT_NAME[suit]} (turn it back down)`}
              aria-pressed={isSel}
              onClick={() => handleCardClick(snapshot.up_card)}
              style={{
                position: 'absolute',
                left: rect.x, top: rect.y, width: rect.w, height: rect.h,
                background: 'transparent',
                border: isSel ? '3px solid #E03048' : 'none',
                borderRadius: isSel ? 6 : 0,
                padding: 0,
                cursor: 'pointer',
                outline: 'none',
              }}
            />
          );
        })()}
        {isHumanBid && (
          <BiddingControls
            state={snapshot}
            seat={HUMAN_SEAT}
            layout={layout}
            onBid={handleBid}
          />
        )}
      </div>
      {isHumanDiscard && (
        <DiscardPrompt
          selectedCardId={selectedDiscard}
          onConfirm={handleDiscardConfirm}
        />
      )}
      <EndOfHandModal
        open={isHandOver}
        // #15: the Lua snapshot's score_summary is {delta_team, delta_score,
        // label, game_over}, not the {result, scoring_team, prev/new_scores}
        // EndOfHandModal needs. Adapt it so the real-game recap isn't empty.
        summary={handSummaryFromSnapshot(snapshot)}
        tricks={snapshot.completed_tricks?.map(t => ({
          winner_seat:  t.winner_seat,
          winning_card: t.winning_card,
        }))}
        partnerHand={snapshot.hands?.[3]}
        trumpSuit={snapshot.trump_suit}
        showPartnerReveal={!!snapshot.post_hand_reveal}
        onContinue={continueHand}
      />
      <GameOverOverlay
        open={isGameOver}
        summary={snapshot.score_summary}
        scores={snapshot.scores}
        winningTeam={snapshot.winning_team}
        onPlayAgain={handlePlayAgain}
      />
      <div data-testid="real-game-status" class="status ready">
        State: <code>{snapshot.current_state}</code>
        {' | turn: '}<code>seat {snapshot.current_turn_seat ?? '—'}</code>
        {snapshot.trump_suit && <> | trump: <code>{snapshot.trump_suit}</code></>}
        {' | scores: '}<code>{snapshot.scores.team_a}-{snapshot.scores.team_b}</code>
      </div>
      {state.error && (
        <div data-testid="real-game-error-banner"
             style={{color:'#E03048',padding:'0.5em',fontSize:12}}>
          Last error: {state.error}
        </div>
      )}
    </div>
  );
}
